import React, { ReactNode, useRef, useState } from "react";
import * as Tooltip from "@radix-ui/react-tooltip";

const ToolTip = ({
  text,
  children,
  propClass = "t-max-w-[500px]",
  delayDuration = 0,
  disableHoverableContent = false,
  keepOpenOnActivation = false,
  open,
  portal,
  ...props
}: Tooltip.TooltipContentProps & {
  children: ReactNode;
  text?: string | ReactNode;
  delayDuration?: number;
  propClass?: string;
  disableHoverableContent?: boolean;
  keepOpenOnActivation?: boolean;
  open?: boolean;
  portal?: HTMLElement;
}) => {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const pointerTypeRef = useRef("");
  const [isOpen, setIsOpen] = useState(false);

  const handlePointerDown = (e: React.PointerEvent) => {
    pointerTypeRef.current = e.pointerType;
  };

  // Radix tooltips only open on hover, so on touch a tap toggles the tip
  // instead — unless the tap lands on a link/button, which keeps its action.
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (keepOpenOnActivation) {
      e.preventDefault();
    }

    if (pointerTypeRef.current !== "touch") return;

    // Only links/buttons/menu items within the trigger count — not ones wrapping it, like an accordion header.
    const interactiveTarget =
      e.target instanceof Element
        ? e.target.closest("a, button, [role^='menuitem']")
        : null;
    const isInteractiveTarget = Boolean(
      interactiveTarget && triggerRef.current?.contains(interactiveTarget)
    );
    if (isInteractiveTarget) return;

    e.preventDefault();
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  return (
    <Tooltip.Provider>
      <Tooltip.Root
        delayDuration={delayDuration}
        disableHoverableContent={disableHoverableContent}
        open={open ?? isOpen}
        onOpenChange={setIsOpen}
      >
        <Tooltip.Trigger
          onPointerDown={handlePointerDown}
          onClick={handleClick}
          ref={triggerRef}
          asChild
        >
          {children}
        </Tooltip.Trigger>
        <Tooltip.Portal container={portal}>
          <Tooltip.Content
            {...props}
            className={`t-bg-neutral-100 t-text-surface t-p-3 t-rounded t-text-body-sm t-z-tooltip t-text-center ${propClass} sm:t-max-w-[var(--radix-tooltip-content-available-width)] t-break-words t-hyphen-auto t-shadow-dark-30`}
            onPointerDownOutside={(e) => {
              if (
                keepOpenOnActivation &&
                e.target &&
                triggerRef.current?.contains?.(e.target as Node)
              ) {
                e.preventDefault();
              }
            }}
          >
            {text}
            <Tooltip.Arrow />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default ToolTip;
